import { useEffect, useState, useSyncExternalStore } from 'react';
import type { Event } from '../types';
import { allInFloor, fitSaved, isSavedRefusal, mergeSaved, SAVED_ID, sameCents, stickerFloor } from '../../shared/saved-price.mjs';
import { eventSlug } from '../../shared/slug.mjs';
import { US_STOREFRONT_ONLY } from './tape';
import { getGeo } from './personal';
import { BUILD_DAY } from './today';

/**
 * SAVED EVENTS, KEPT ON THIS DEVICE (2026-10-19). No account and no email: the list lives in this
 * browser's localStorage ('tixinfo.saved.v1', at most 30, newest first), with a backup in an
 * HttpOnly first-party cookie only /api/saved receives (see backUp below). What leaves the device:
 * event ids for today's prices, and the backup's ids, saved prices, days and two flags (outside
 * the US; price on screen); functions/api/saved.ts stores nothing. The privacy policy says so
 * (src/pages/Legal.tsx).
 *
 * Every read and write is guarded: storage can be blocked, full, or throw on access (private
 * windows, "block site data"). Saving then says "Can't save in this browser" and nothing breaks.
 *
 * AFTER THE BROWSER ERASED THE LIST (Safari, seven days away; the cookie stays): a save that finds
 * no list at all keeps its show at once, then reads the backup back and merges it in (what was
 * saved since comes first), and nothing is posted before that, since a post replaces the backup
 * (restoreSaved). A show removed while the backup is read stays removed, and a list the visitor
 * emptied is stored as [] and never merges anything back.
 *
 * The lists render AFTER MOUNT only. useSyncExternalStore's server snapshot is empty, so the
 * prerender, the edge render and the first client render produce identical markup; the saved
 * state appears right after hydration. (Reading storage during the first render threw the server
 * markup away on the event page: the party-size hydration bail-out the 2026-10-18 audit measured.)
 *
 * "Since you saved" compares fees-included prices only (shared/saved-price.mjs), taken from the
 * event's BUILT offers (never the live HelloTickets refresh, which the daily D1 price never sees),
 * and without HelloTickets for visitors outside the US, the same rule as the event page's chart.
 * A saved price is only ever compared when it was also the price on screen (`seen`): when the
 * live refresh had moved the page's floor away from the built one, the visitor saw another number,
 * and "down $X since you saved it" would describe a price they never saw (shownIsBuilt below).
 */

/** What a list remembers about one event: enough to draw its row before any request. */
export interface EventSnap {
  id: string;
  slug: string;
  name: string;
  artist?: string;
  teams?: string[];
  category: string;
  /** the event's local date, YYYY-MM-DD */
  date: string;
  time?: string;
  venue: string;
  city: string;
  imageUrl?: string;
}

export interface SavedItem extends EventSnap {
  /** fees-included floor when it was saved (USD), null when no price included fees */
  floorAtSave: number | null;
  /** before-fees floor when it was saved, only when there was no fees-included one */
  stickerAtSave: number | null;
  /** HelloTickets was left out (a visitor outside the US): only a floor taken the same way compares */
  intl: boolean;
  /**
   * The saved price was the one the page showed (a card, the Saved page, or an event page whose live
   * refresh had not moved its floor). Only such a price is compared later; missing = not compared.
   */
  seen?: boolean;
  /** the visitor's local date it was saved, YYYY-MM-DD */
  savedAt: string;
}

const SAVED_KEY = 'tixinfo.saved.v1';
export const SAVED_MAX = 30;

/**
 * Set while this device's list may lack rows its backup still holds: a save found no list here at
 * all (erased by the browser, or a first save ever) and the backup has not been read back since.
 * Kept in localStorage, so a page closed before the answer still merges later (the next change, or
 * the Saved page). While it is set, no backup is posted (sendBackup). Its value lists the shows
 * removed here meanwhile (JSON ids): the merge leaves the backup's copies of them out, so a show
 * removed while the backup was being read stays removed.
 */
const MERGE_KEY = 'tixinfo.saved.merge';
/** the pending merge, for this page life, when this browser would not store it */
let mergeHere: string[] | null = null;
/** The pending merge's removed ids ([] = none), or null when no merge is pending. */
const pendingMerge = (): string[] | null => {
  if (mergeHere) return mergeHere;
  let raw: string | null;
  try {
    raw = localStorage.getItem(MERGE_KEY);
  } catch {
    return null;
  }
  if (raw === null) return null;
  try {
    const ids: unknown = JSON.parse(raw);
    if (Array.isArray(ids)) return ids.filter((id): id is string => typeof id === 'string');
  } catch {
    /* not a list: still pending, with no removals known */
  }
  return [];
};
const mergePending = () => pendingMerge() !== null;
const keepMerge = (removed: string[]) => {
  try {
    localStorage.setItem(MERGE_KEY, JSON.stringify(removed.slice(-2 * SAVED_MAX)));
    mergeHere = null;
  } catch {
    mergeHere = removed;
  }
};
const markMerge = () => keepMerge([]);
const clearMerge = () => {
  mergeHere = null;
  try {
    localStorage.removeItem(MERGE_KEY);
  } catch {
    /* nothing stored, nothing to clear */
  }
};
/** No saved list on this device at all: never saved, or erased by the browser (an emptied one is []). */
const listAbsent = () => {
  try {
    return localStorage.getItem(SAVED_KEY) === null;
  } catch {
    return false;
  }
};

const pad = (n: number) => String(n).padStart(2, '0');
/** The visitor's local date, YYYY-MM-DD (never UTC: a US evening is already tomorrow in UTC). */
export const localDay = (d = new Date()) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const isDay = (v: unknown): v is string => typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v);
/** A price field: a positive number, or none (null; a missing field counts as none). */
const isNum = (v: unknown) => v == null || (typeof v === 'number' && Number.isFinite(v) && v > 0);

/** A stored row is used only if it still has the shape it was written with. */
export const isSnap = (v: unknown): v is EventSnap => {
  const o = v as Record<string, unknown> | null;
  return (
    !!o &&
    typeof o === 'object' &&
    typeof o.id === 'string' &&
    SAVED_ID.test(o.id) &&
    typeof o.slug === 'string' &&
    typeof o.name === 'string' &&
    typeof o.venue === 'string' &&
    typeof o.city === 'string' &&
    typeof o.category === 'string' &&
    isDay(o.date)
  );
};
const isSavedItem = (v: unknown): v is SavedItem => {
  const o = v as Record<string, unknown>;
  return isSnap(v) && isNum(o.floorAtSave) && isNum(o.stickerAtSave) && isDay(o.savedAt) && (o.seen === undefined || typeof o.seen === 'boolean');
};

const EMPTY: never[] = [];
/** The server's (and the hydrating client's) view of every device list: empty. */
export const emptySnapshot = () => EMPTY;

/**
 * One localStorage list behind useSyncExternalStore: cached until it changes, synced across tabs
 * through the 'storage' event, never throwing. `keep` filters rows as they are read.
 */
export const listStore = <T extends { id: string }>(
  key: string,
  max: number,
  valid: (v: unknown) => v is T,
  keep?: (row: T) => boolean,
  /** after this tab changed the list (not another tab) */
  onSet?: (rows: T[]) => void
) => {
  let cache: T[] | null = null;
  const listeners = new Set<() => void>();
  const emit = () => listeners.forEach((l) => l());
  const read = (): T[] => {
    try {
      const raw = localStorage.getItem(key);
      const parsed: unknown = raw ? JSON.parse(raw) : [];
      const rows = Array.isArray(parsed) ? parsed.filter(valid) : [];
      return (keep ? rows.filter(keep) : rows).slice(0, max);
    } catch {
      return [];
    }
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key !== key && e.key !== null) return;
    cache = null;
    emit();
  };
  return {
    get: (): T[] => (cache ??= read()),
    subscribe: (cb: () => void) => {
      listeners.add(cb);
      if (listeners.size === 1) window.addEventListener('storage', onStorage);
      return () => {
        listeners.delete(cb);
        if (!listeners.size) window.removeEventListener('storage', onStorage);
      };
    },
    /**
     * false when the browser would not store it (blocked or full storage): nothing changes then.
     * An emptied list is stored as [] (not removed), so "cleared" stays distinguishable from
     * "erased by the browser" (restoreSaved only answers the second).
     */
    set: (next: T[]): boolean => {
      const rows = next.slice(0, max);
      try {
        localStorage.setItem(key, JSON.stringify(rows));
      } catch {
        return false;
      }
      cache = rows;
      emit();
      onSet?.(rows);
      return true;
    },
  };
};

/**
 * The saved list's backup (functions/api/saved.ts): the ids, saved prices (and whether each was
 * the one on screen), days and storefront rule go to /api/saved, which answers with an HttpOnly
 * cookie only that address ever receives — Safari erases localStorage after seven days away, not
 * cookies a server sets. Nothing else is sent; nothing is stored on our side.
 *
 * When: 2 s after this tab last changed the list (the /api/ rate rule), at once when the list was
 * emptied (an emptied list clears the cookie, as the privacy policy promises), and right away when
 * the tab is hidden or closed with a change still waiting; keepalive lets that request finish after
 * the page is gone. (Before, "Clear saved" and closing the tab within 2 s left the old cookie, and
 * the next visit to the Saved page brought the cleared list back.)
 *
 * Never while a merge is pending (MERGE_KEY): the post would replace a backup this device has not
 * read back yet. The backup is read and merged instead, and that posts the whole list.
 */
let backupTimer = 0;
let pendingBackup: SavedItem[] | null = null;
let backupHooked = false;
const sendBackup = () => {
  window.clearTimeout(backupTimer);
  const rows = pendingBackup;
  pendingBackup = null;
  if (!rows) return;
  if (mergePending()) {
    void restoreSaved();
    return;
  }
  try {
    void fetch('/api/saved', {
      method: 'POST',
      keepalive: true,
      credentials: 'same-origin',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({
        items: rows.map(({ id, floorAtSave, stickerAtSave, savedAt, intl, seen }) => ({ id, floorAtSave, stickerAtSave, savedAt, intl, seen: seen === true })),
      }),
    }).catch(() => {});
  } catch {
    /* no backup this time; the next change tries again */
  }
};
const backUp = (rows: SavedItem[]) => {
  pendingBackup = rows;
  if (!backupHooked) {
    backupHooked = true;
    try {
      window.addEventListener('pagehide', sendBackup);
      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') sendBackup();
      });
    } catch {
      /* the timer still sends it */
    }
  }
  window.clearTimeout(backupTimer);
  if (!rows.length) sendBackup();
  else backupTimer = window.setTimeout(sendBackup, 2000);
};

export const savedStore = listStore<SavedItem>(SAVED_KEY, SAVED_MAX, isSavedItem, undefined, backUp);

/** This device's saved events, newest first: [] on the server and until hydration is done. */
export const useSaved = (): SavedItem[] => useSyncExternalStore(savedStore.subscribe, savedStore.get, emptySnapshot);

/** The event as a list row: its name, date and place, nothing personal. */
export const snapOf = (e: Event): EventSnap => ({
  id: e.id,
  slug: eventSlug(e),
  name: e.name,
  ...(e.artist ? { artist: e.artist } : {}),
  ...(e.teams?.length ? { teams: e.teams } : {}),
  category: e.category,
  date: e.date,
  ...(e.time ? { time: e.time } : {}),
  venue: e.venue,
  city: e.city,
  ...(e.imageUrl ? { imageUrl: e.imageUrl } : {}),
});

/** Marketplaces left out of this visitor's floors (src/lib/tape.ts: HelloTickets outside the US). */
export const dropFor = (intl: boolean) => (intl ? US_STOREFRONT_ONLY : undefined);

/**
 * The event's comparable prices for this visitor, from its built offers. A card's slim row has no
 * offers, only its headline price and that price's fee basis: then that, and nothing when the
 * headline came from a marketplace this visitor's floors leave out.
 */
export const floorsOf = (e: Pick<Event, 'offers' | 'lowestPrice' | 'lowestAllIn' | 'priceSource'>, intl: boolean) => {
  const drop = dropFor(intl);
  if (e.offers?.length) return { floor: allInFloor(e.offers, drop), sticker: stickerFloor(e.offers, drop) };
  const p = typeof e.lowestPrice === 'number' && e.lowestPrice > 0 ? e.lowestPrice : null;
  if (p === null || (drop && e.priceSource && drop.includes(e.priceSource))) return { floor: null, sticker: null };
  return e.lowestAllIn ? { floor: p, sticker: null } : { floor: null, sticker: p };
};

/**
 * Is the fees-included floor the event page SHOWS (its board, after the live HelloTickets refresh)
 * the floor this visitor's lists record (the built offers, floorsOf)? Only then is a recorded or
 * compared price the one on screen. `settled`: the live refresh has answered, or the event has
 * none; before that, and when it never answers, the answer is no.
 */
export const shownIsBuilt = (
  built: Pick<Event, 'offers' | 'lowestPrice' | 'lowestAllIn' | 'priceSource'>,
  shown: Pick<Event, 'offers' | 'lowestPrice' | 'lowestAllIn' | 'priceSource'>,
  settled: boolean,
  intl: boolean | null
) => intl !== null && settled && sameCents(floorsOf(shown, false).floor, floorsOf(built, intl).floor);

/**
 * Is the price an event card shows (its headline, src/components/EventCard.tsx) the floor a save
 * records for this visitor? Not when the headline is a before-fees price beside a dearer
 * fees-included floor, or a HelloTickets US price left out for a visitor outside the US: then the
 * visitor saved while looking at another number, and nothing is compared with it later.
 */
export const cardPriceIsFloor = (e: Pick<Event, 'offers' | 'lowestPrice' | 'lowestAllIn' | 'priceSource'>, intl: boolean) => {
  const { floor, sticker } = floorsOf(e, intl);
  return sameCents(floor ?? sticker, typeof e.lowestPrice === 'number' && e.lowestPrice > 0 ? e.lowestPrice : null);
};

/**
 * Put a row at the top (replacing the event's old one). Full: the shows that are over go first, then
 * the longest-saved (shared/saved-price.mjs fitSaved). With no list on this device at all, the
 * backup may still hold one (the browser erased it): the row is kept at once, and the backup is
 * read back and merged in before anything is posted (restoreSaved).
 */
const putSaved = (item: SavedItem): boolean => {
  const fresh = listAbsent() && !mergePending();
  if (fresh) markMerge();
  const next = fitSaved([item, ...savedStore.get().filter((s) => s.id !== item.id)], localDay(), SAVED_MAX);
  if (!savedStore.set(next)) {
    if (fresh) clearMerge();
    return false;
  }
  if (mergePending()) void restoreSaved();
  return true;
};

/**
 * Save an event (it moves to the top if it was saved before). False = this browser keeps nothing.
 * `seen`: the price recorded is the one the visitor was shown (SavedItem.seen).
 */
export const saveEvent = (e: Event, intl: boolean, seen: boolean): boolean => {
  const { floor, sticker } = floorsOf(e, intl);
  return putSaved({ ...snapOf(e), floorAtSave: floor, stickerAtSave: floor === null ? sticker : null, intl, seen, savedAt: localDay() });
};

/**
 * Save a list row (a recently viewed event) with today's price from /api/saved as its baseline:
 * the price that row showed. With no price at hand, no baseline and nothing compared later (an
 * old view's price would invent a "since you saved", a missing one a false "Now listed").
 */
export const saveSnap = (snap: EventSnap, price: { floor: number | null; sticker: number | null } | null, intl: boolean): boolean => {
  const { id, slug, name, artist, teams, category, date, time, venue, city, imageUrl } = snap;
  return putSaved({
    ...{ id, slug, name, category, date, venue, city },
    ...(artist ? { artist } : {}),
    ...(teams?.length ? { teams } : {}),
    ...(time ? { time } : {}),
    ...(imageUrl ? { imageUrl } : {}),
    floorAtSave: price?.floor ?? null,
    stickerAtSave: price && price.floor === null ? price.sticker : null,
    intl,
    seen: price !== null,
    savedAt: localDay(),
  });
};

/** Remove a show. While a merge waits for the backup, the backup's copy of it stays out of the merge too. */
export const unsaveEvent = (id: string): boolean => {
  if (!savedStore.set(savedStore.get().filter((s) => s.id !== id))) return false;
  const removed = pendingMerge();
  if (removed && !removed.includes(id)) keepMerge([...removed, id]);
  return true;
};

/**
 * "Clear saved": the list emptied on purpose. Stored as [] (it never comes back from the backup),
 * and the backup is cleared at once, rows a pending merge has not read back yet included: the
 * visitor asked for nothing to be kept.
 */
export const clearSaved = (): boolean => {
  const pending = pendingMerge();
  clearMerge();
  if (savedStore.set([])) return true;
  if (pending) keepMerge(pending);
  return false;
};

/**
 * The toast's Undo, after a removal or "Clear saved": the list as it was, then whatever joined it
 * since (mergeSaved). A merge can land in between (removing a show retries a backup read that was
 * slow or failed), and the copy taken before it lacks the backup's rows: storing that copy would
 * post it and lose them for good.
 */
export const undoSaved = (before: SavedItem[]): boolean => savedStore.set(mergeSaved(before, savedStore.get(), localDay(), SAVED_MAX));

/** An event that moved to a new id keeps its saved price and date under the new one. */
export const moveSaved = (from: string, to: { id: string; slug: string }) => {
  const list = savedStore.get();
  if (!list.some((s) => s.id === from)) return;
  const kept = list.filter((s) => s.id !== to.id);
  savedStore.set(kept.map((s) => (s.id === from ? { ...s, id: to.id, slug: to.slug } : s)));
};

let intlOnce: Promise<boolean> | null = null;
/** Is this visitor outside the US? The event page's own rule (edge geo; US when unknown), asked once per page life. */
export const visitorIntl = (): Promise<boolean> =>
  (intlOnce ??= getGeo()
    .then((g) => (g?.country ? g.country !== 'US' : false))
    .catch(() => false));

/** visitorIntl() for a component: null until known. */
export const useIntl = (): boolean | null => {
  const [intl, setIntl] = useState<boolean | null>(null);
  useEffect(() => {
    let alive = true;
    void visitorIntl().then((v) => {
      if (alive) setIntl(v);
    });
    return () => {
      alive = false;
    };
  }, []);
  return intl;
};

/** The visitor's local date: the build day on the server and while hydrating (both renders agree), the real one after mount. */
export const useLocalToday = () => {
  const [day, setDay] = useState(BUILD_DAY);
  useEffect(() => setDay(localDay()), []);
  return day;
};

/** Whole days from `today` to an event's date (0 = today). */
export const daysUntil = (date: string, today: string) =>
  Math.round((Date.parse(`${date}T12:00:00Z`) - Date.parse(`${today}T12:00:00Z`)) / 864e5);

/** "0-1", "2-7", "8-14", "15-30", "31+": how far out, for analytics (never the event itself). */
export const daysOutBucket = (date: string, today = localDay()) => {
  const d = daysUntil(date, today);
  return d <= 1 ? '0-1' : d <= 7 ? '2-7' : d <= 14 ? '8-14' : d <= 30 ? '15-30' : '31+';
};

/**
 * A GA4 event, and the same mark on the Clarity session; never throws, never blocks. Parameters
 * never carry an event id or anything that identifies a visitor: a category and a days-out bucket
 * at most. (src/lib/track.ts has the partner-click event; this is for the device lists until the
 * measure layer offers a generic helper.)
 */
export const gaEvent = (name: string, params: Record<string, string | number> = {}) => {
  try {
    const w = window as unknown as { gtag?: (...a: unknown[]) => void; clarity?: (...a: unknown[]) => void };
    w.gtag?.('event', name, params);
    w.clarity?.('event', name);
  } catch {
    /* analytics never blocks */
  }
};

/**
 * What /api/saved answers for one id (functions/api/saved.ts). Every asked id gets an answer; an
 * event the database no longer has is just { id, current, status: 'gone' } and its row keeps the
 * name, date and place it was saved with.
 */
export interface SavedPrice {
  /** the id that was asked for */
  id: string;
  /** the id the event lives under now (an event that moved), else the same */
  current: string;
  /** live: on sale; soon: announced, not listed yet; gone: no longer listed */
  status: 'live' | 'soon' | 'gone';
  path?: string;
  name?: string;
  artist?: string | null;
  category?: string;
  /** today's date for it: a rescheduled show has moved */
  date?: string;
  time?: string | null;
  venue?: string;
  city?: string;
  image?: string | null;
  /** fees-included floor today (USD) */
  floor: number | null;
  /** before-fees floor, only when nothing includes fees */
  sticker: number | null;
  /** the act's or team's page, when it still lists dates */
  more?: { path: string; name: string } | null;
}

const EVENT_PATH = /^\/[a-z0-9-]{1,120}\/event\/[a-z]{2}-[A-Za-z0-9_-]{1,60}$/;
const HUB_PATH = /^\/(artist|team)\/[a-z0-9-]{1,120}$/;
const isPrice = (v: unknown): v is SavedPrice => {
  const o = v as Record<string, unknown> | null;
  const more = o?.more as Record<string, unknown> | null | undefined;
  return (
    !!o &&
    typeof o.id === 'string' &&
    typeof o.current === 'string' &&
    (o.status === 'live' || o.status === 'soon' || o.status === 'gone') &&
    isNum(o.floor ?? null) &&
    isNum(o.sticker ?? null) &&
    (o.path === undefined || (typeof o.path === 'string' && EVENT_PATH.test(o.path))) &&
    (o.date === undefined || isDay(o.date)) &&
    (more == null || (typeof more.path === 'string' && HUB_PATH.test(more.path) && typeof more.name === 'string'))
  );
};

/** Answers fetched this page life, per storefront rule (null = asked, not answered): removing a row never refetches. */
const priceCache = new Map<string, SavedPrice | null>();
const cacheKey = (drop: string, id: string) => `${drop}|${id}`;

export type PricesState = { status: 'idle' | 'loading' | 'ok' | 'error'; prices: Record<string, SavedPrice> };

/**
 * Today's prices for these events, from one request for the ids not fetched yet. `intl` null = the
 * storefront rule is not known yet: nothing is fetched until it is. A failed request leaves the
 * lists as they are, without prices.
 */
export const useSavedPrices = (ids: string[], intl: boolean | null): PricesState => {
  const drop = intl ? US_STOREFRONT_ONLY.join(',') : '';
  const want = intl === null ? '' : [...new Set(ids)].sort().join(',');
  const [state, setState] = useState<PricesState>({ status: 'idle', prices: {} });
  useEffect(() => {
    if (intl === null) return;
    const all = want ? want.split(',') : [];
    const have = (): Record<string, SavedPrice> => {
      const out: Record<string, SavedPrice> = {};
      for (const id of all) {
        const p = priceCache.get(cacheKey(drop, id));
        if (p) out[id] = p;
      }
      return out;
    };
    const missing = all.filter((id) => !priceCache.has(cacheKey(drop, id)));
    if (!missing.length) {
      setState({ status: all.length ? 'ok' : 'idle', prices: have() });
      return;
    }
    let alive = true;
    setState((s) => ({ status: 'loading', prices: s.prices }));
    fetch(`/api/saved?ids=${encodeURIComponent(missing.join(','))}${drop ? `&drop=${encodeURIComponent(drop)}` : ''}`, { credentials: 'omit' })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(String(r.status)))))
      .then((d: { items?: unknown }) => {
        if (!Array.isArray(d?.items)) throw new Error('bad answer');
        for (const p of d.items) if (isPrice(p)) priceCache.set(cacheKey(drop, p.id), p);
        for (const id of missing) if (!priceCache.has(cacheKey(drop, id))) priceCache.set(cacheKey(drop, id), null);
        if (alive) setState({ status: 'ok', prices: have() });
      })
      .catch(() => {
        if (alive) setState({ status: 'error', prices: have() });
      });
    return () => {
      alive = false;
    };
  }, [want, drop, intl]);
  return state;
};

const RESTORE_TRIED = 'tixinfo.saved.restore';

/** The backup's rows as /api/saved?restore=1 answers them: today's details, under each event's current id, once. */
const backupRows = (d: { saved?: unknown } | null): SavedItem[] => {
  const items: SavedItem[] = [];
  for (const v of Array.isArray(d?.saved) ? d.saved : []) {
    const o = v as SavedPrice & { floorAtSave?: unknown; stickerAtSave?: unknown; savedAt?: unknown; intl?: unknown; seen?: unknown };
    if (
      !isPrice(o) ||
      !o.path ||
      !o.name ||
      !o.date ||
      !o.venue ||
      !o.city ||
      typeof o.category !== 'string' ||
      !isDay(o.savedAt) ||
      !isNum(o.floorAtSave) ||
      !isNum(o.stickerAtSave) ||
      // two saved ids that became one event (it moved): the first, the newer save, stays
      items.some((s) => s.id === o.current)
    )
      continue;
    items.push({
      id: o.current,
      slug: o.path.split('/')[1],
      name: o.name,
      ...(o.artist ? { artist: o.artist } : {}),
      category: o.category,
      date: o.date,
      ...(o.time ? { time: o.time } : {}),
      venue: o.venue,
      city: o.city,
      ...(o.image ? { imageUrl: o.image } : {}),
      floorAtSave: typeof o.floorAtSave === 'number' ? o.floorAtSave : null,
      stickerAtSave: typeof o.floorAtSave !== 'number' && typeof o.stickerAtSave === 'number' ? o.stickerAtSave : null,
      intl: o.intl === true,
      seen: o.seen === true,
      savedAt: o.savedAt,
    });
  }
  return items;
};

let restoring: Promise<boolean> | null = null;

/**
 * The saved list back from its backup cookie (see backUp), after the browser erased this site's
 * storage. Two callers:
 * - The Saved page, when this browser has no saved list at all: once per tab session. A list the
 *   visitor emptied is stored as [] and never comes back, even if the cookie's clearing request
 *   was lost.
 * - A merge (MERGE_KEY): a save found no list at all and kept its show. The backup's rows join the
 *   list behind what was saved since (mergeSaved), then the whole list is posted. Until the backup
 *   has answered nothing is posted; a passing failure (offline, a database hiccup, a 403 from
 *   Cloudflare) leaves the merge for the next change or the Saved page. Only the function's own
 *   refusal (shared/saved-price.mjs SAVED_REFUSAL: this browser cannot read its backup back, so
 *   nothing in it can return) ends the merge without one.
 * One request at a time. Events the database no longer has stay lost. Resolves true when rows came
 * back.
 */
export const restoreSaved = (): Promise<boolean> =>
  (restoring ??= runRestore().finally(() => {
    restoring = null;
  }));

const runRestore = async (): Promise<boolean> => {
  if (!mergePending()) {
    if (savedStore.get().length) return false;
    try {
      // emptied on this device (or storage blocked, which throws): nothing to restore, or nowhere to put it
      if (localStorage.getItem(SAVED_KEY) !== null) return false;
      if (sessionStorage.getItem(RESTORE_TRIED)) return false;
      sessionStorage.setItem(RESTORE_TRIED, '1');
    } catch {
      return false;
    }
  }
  let backup: SavedItem[];
  try {
    const r = await fetch('/api/saved?restore=1', { credentials: 'same-origin', cache: 'no-store' });
    if (!r.ok) {
      // A passing failure (a database hiccup answers 503, a Cloudflare challenge or block page 403),
      // like being offline below: a pending merge waits for the next change or the Saved page, and
      // nothing is posted meanwhile. Only the function's own refusal (SAVED_REFUSAL) is final: this
      // browser cannot read its backup back, so nothing in it can return here, and posting this
      // device's list loses nothing (and the backup does not stay frozen).
      if (r.status === 403 && mergePending()) {
        const type = r.headers.get('content-type');
        const body: unknown = type?.includes('json') ? await r.json().catch(() => null) : null;
        if (isSavedRefusal(r.status, type, body) && mergePending()) {
          clearMerge();
          backUp(savedStore.get());
        }
      }
      return false;
    }
    backup = backupRows((await r.json()) as { saved?: unknown } | null);
  } catch {
    return false;
  }
  // Read now, not before the request: whatever was saved, removed or emptied meanwhile counts.
  const mine = savedStore.get();
  const removed = pendingMerge();
  if (removed) {
    clearMerge();
    const next = mergeSaved(mine, backup, localDay(), SAVED_MAX, removed);
    // stored, then posted whole (backUp)
    if (!savedStore.set(next)) {
      keepMerge(removed);
      return false;
    }
    return next.length > mine.length;
  }
  // the Saved page's case: only into a list that is still erased (nothing saved or emptied meanwhile)
  if (!backup.length || mine.length || !listAbsent()) return false;
  return savedStore.set(backup);
};
